import React, { useState } from "react";
import { observer } from "mobx-react";
import { generatePath, useNavigate } from "react-router-dom";
import AuthStore from "stores/AuthStore";
import Button from "components/Button";
import { WithTranslation, withTranslation } from "react-i18next";
import withGlobalPopup, {
  GlobalPopupProps,
} from "components/Popup/withGlobalPopup";

import ROUTES from "navigation/routes";
import {
  Background,
  Container,
  LogoContainer,
  Logo,
  ItemWrapper,
  LoadContainer,
  InnerWrapper,
} from "./styles";
import Spinner from "components/Spinner/Spinner";
import {
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  OutlinedInput,
  Typography,
  Link,
} from "@mui/material";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { composeValidators, isEmptyPasswordNew } from "utils/validation";

type Props = GlobalPopupProps & WithTranslation;

enum ForgotPasswordStep {
  EMAIL,
  VERIFICATION,
  NEW_PASSWORD,
  CONFIRMATION,
}

interface FormValues {
  email: string;
  verificationCode: number | null;
  newPassword: string;
  confirmPassword: string;
  showNewPassword: boolean;
  showConfirmPassword: boolean;
}

const ForgotPassword: React.FC<Props> = observer(props => {
  const t: (key: string) => string = props.t;
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState<ForgotPasswordStep>(
    ForgotPasswordStep.EMAIL,
  );
  const [loading, setLoading] = useState(false);
  const [formValues, setFormValues] = useState<FormValues>({
    email: AuthStore.email,
    verificationCode: null,
    newPassword: "",
    confirmPassword: "",
    showNewPassword: false,
    showConfirmPassword: false,
  });
  const [errors, setErrors] = useState({
    email: "",
    verificationCode: "",
    password: "",
  });

  const validateEmail = () => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formValues.email)) {
      setErrors(prev => ({
        ...prev,
        email: t("forgot-password.enter-valid-email"),
      }));
      return false;
    }
    return true;
  };

  const validateVerificationCode = () => {
    if (formValues.verificationCode === null) {
      setErrors(prev => ({
        ...prev,
        verificationCode: t("forgot-password.enter-code"),
      }));
      return false;
    }
    const codeString = formValues.verificationCode.toString();
    const isNumeric = /^\d+$/.test(codeString);

    if (!isNumeric || codeString.length !== 6) {
      setErrors(prev => ({
        ...prev,
        verificationCode: t("forgot-password.enter-code"),
      }));
      return false;
    }
    return true;
  };

  const validatePasswords = () => {
    const { newPassword, confirmPassword } = formValues;

    const errNewPassword = composeValidators(isEmptyPasswordNew)(newPassword);

    const errConfirmPassword =
      composeValidators(isEmptyPasswordNew)(confirmPassword);

    if (errNewPassword || errConfirmPassword) {
      setErrors(prev => ({
        ...prev,
        password: t(errNewPassword || errConfirmPassword),
      }));
      return;
    }

    if (formValues.newPassword !== formValues.confirmPassword) {
      setErrors(prev => ({
        ...prev,
        password: t("forgot-password.pass-not-match"),
      }));
      return false;
    }

    const passwordValidation = true; // add more FE validations if needed. Validations from BE being applied.

    if (!passwordValidation) {
      setErrors(prev => ({
        ...prev,
        password: t("forgot-password.password-lenght"),
      }));
      return false;
    }
    return true;
  };

  const handleChange =
    (prop: keyof typeof formValues) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setErrors({ email: "", verificationCode: "", password: "" });
      setFormValues({ ...formValues, [prop]: event.target.value });
    };

  const handleTogglePasswordVisibility = (
    field: "showNewPassword" | "showConfirmPassword",
  ) => {
    setFormValues({ ...formValues, [field]: !formValues[field] });
  };

  const handleSignIn = () => {
    navigate(generatePath(ROUTES.SIGN_IN));
  };

  const handleSubmit = async () => {
    setErrors({ email: "", verificationCode: "", password: "" });

    switch (currentStep) {
      case ForgotPasswordStep.EMAIL:
        if (formValues.email && validateEmail()) {
          try {
            setLoading(true);
            await AuthStore.forgotPassword(formValues.email);
            setCurrentStep(ForgotPasswordStep.VERIFICATION);
          } catch (error: any) {
            // we don't need to show the error message here.
            setCurrentStep(ForgotPasswordStep.VERIFICATION);
          } finally {
            setLoading(false);
          }
        }
        break;

      case ForgotPasswordStep.VERIFICATION:
        if (formValues.verificationCode && validateVerificationCode()) {
          try {
            setLoading(true);
            await AuthStore.validateCode(formValues.verificationCode);
            setCurrentStep(ForgotPasswordStep.NEW_PASSWORD);
          } catch (error: any) {
            const errMessage =
              error?.message === "Incorrect user or code"
                ? t("forgot-password.incorrectUserOrCode")
                : error?.message === "Token is expired" ||
                  error?.message === "Your code has expired"
                ? t("forgot-password.tokenExpired")
                : t("forgot-password.something-wrong");
            setErrors(prev => ({
              ...prev,
              verificationCode: errMessage,
            }));
          } finally {
            setLoading(false);
          }
        }
        break;

      case ForgotPasswordStep.NEW_PASSWORD:
        if (validatePasswords()) {
          try {
            setLoading(true);
            const res = await AuthStore.passwordReset(formValues.newPassword);
            if (res) setCurrentStep(ForgotPasswordStep.CONFIRMATION);
          } catch (error: any) {
            const errMessage = error?.message.includes(
              "password must be minimum",
            )
              ? t("forgot-password.password-lenght")
              : error?.message === "Incorrect user or code"
              ? t("forgot-password.incorrectUserOrCode")
              : error?.message === "Token is expired" ||
                error?.message === "Your code has expired"
              ? t("forgot-password.incorrectUserOrCode")
              : t("forgot-password.something-wrong");
            setErrors(prev => ({ ...prev, password: errMessage }));
          } finally {
            setLoading(false);
          }
        }
        break;

      case ForgotPasswordStep.CONFIRMATION:
        handleSignIn();
        break;
    }
  };

  const renderContent = () => {
    if (loading) {
      return (
        <LoadContainer>
          <Spinner large />
        </LoadContainer>
      );
    }

    switch (currentStep) {
      case ForgotPasswordStep.EMAIL:
        return (
          <InnerWrapper>
            <Typography
              variant="body1"
              sx={{ textAlign: "start", margin: "12px" }}
            >
              {t("forgot-password.enter-valid-email")}
            </Typography>

            <ItemWrapper>
              <FormControl
                variant="outlined"
                fullWidth
                margin="normal"
                error={!!errors.email}
              >
                <InputLabel htmlFor="forgot-input-email">Email</InputLabel>
                <OutlinedInput
                  id="forgot-input-email"
                  label="Email"
                  placeholder="Email"
                  value={formValues.email}
                  onChange={handleChange("email")}
                  endAdornment={
                    <InputAdornment position="end">
                      {errors.email ? (
                        <ErrorOutlineIcon
                          color="error"
                          fontSize="large"
                          id="sign-in-icon-error-email"
                        />
                      ) : (
                        formValues.email &&
                        validateEmail() && (
                          <CheckCircleOutlineIcon
                            fontSize="large"
                            color="primary"
                            id="sign-in-icon-validator-email"
                          />
                        )
                      )}
                    </InputAdornment>
                  }
                />

                {errors.email && (
                  <Typography
                    color="error"
                    variant="caption"
                    fontSize=".8rem"
                    margin=".6rem 0"
                  >
                    {errors.email}
                  </Typography>
                )}
              </FormControl>
            </ItemWrapper>
          </InnerWrapper>
        );

      case ForgotPasswordStep.VERIFICATION:
        return (
          <InnerWrapper>
            <Typography
              variant="body1"
              sx={{ textAlign: "start", marginBottom: "10px" }}
            >
              {t("forgot-password.reset-code-sent")}
            </Typography>
            <ItemWrapper>
              <FormControl
                variant="outlined"
                fullWidth
                margin="normal"
                error={!!errors.verificationCode}
              >
                <InputLabel htmlFor="verification-code">
                  {t("forgot-password.code")}
                </InputLabel>
                <OutlinedInput
                  id="verification-code"
                  label={t("forgot-password.code")}
                  placeholder={t("forgot-password.code")}
                  value={formValues.verificationCode ?? ""}
                  onChange={e => {
                    const numericValue = e.target.value
                      .replace(/[^0-9]/g, "")
                      .slice(0, 6);
                    setFormValues({
                      ...formValues,
                      verificationCode: numericValue
                        ? parseInt(numericValue)
                        : null,
                    });
                  }}
                  inputProps={{
                    maxLength: 6,
                    pattern: "[0-9]*",
                    inputMode: "numeric",
                  }}
                />

                {errors.verificationCode && (
                  <Typography color="error" variant="caption" fontSize=".8rem">
                    {errors.verificationCode}
                  </Typography>
                )}
              </FormControl>
            </ItemWrapper>
          </InnerWrapper>
        );

      case ForgotPasswordStep.NEW_PASSWORD:
        return (
          <InnerWrapper>
            <Typography
              variant="body1"
              sx={{ textAlign: "start", marginBottom: "10px" }}
            >
              {t("forgot-password.enter-new-password")}
            </Typography>
            <ItemWrapper>
              <FormControl
                variant="outlined"
                fullWidth
                margin="normal"
                error={!!errors.password}
              >
                <InputLabel htmlFor="new-password">
                  {t("forgot-password.new-password")}
                </InputLabel>
                <OutlinedInput
                  id="forgot-password.new-password"
                  type={formValues.showNewPassword ? "text" : "password"}
                  label={t("forgot-password.new-password")}
                  placeholder={t("forgot-password.new-password")}
                  value={formValues.newPassword}
                  onChange={handleChange("newPassword")}
                  endAdornment={
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() =>
                          handleTogglePasswordVisibility("showNewPassword")
                        }
                        edge="end"
                      >
                        {formValues.showNewPassword ? (
                          <VisibilityOff />
                        ) : (
                          <Visibility />
                        )}
                      </IconButton>
                    </InputAdornment>
                  }
                />
              </FormControl>
            </ItemWrapper>
            <ItemWrapper>
              <FormControl
                variant="outlined"
                fullWidth
                margin="normal"
                error={!!errors.password}
              >
                <InputLabel htmlFor="confirm-password">
                  {t("forgot-password.repeat-new-password")}
                </InputLabel>
                <OutlinedInput
                  id="confirm-password"
                  type={formValues.showConfirmPassword ? "text" : "password"}
                  label={t("forgot-password.repeat-new-password")}
                  placeholder={t("forgot-password.repeat-new-password")}
                  value={formValues.confirmPassword}
                  onChange={handleChange("confirmPassword")}
                  endAdornment={
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() =>
                          handleTogglePasswordVisibility("showConfirmPassword")
                        }
                        edge="end"
                      >
                        {formValues.showConfirmPassword ? (
                          <VisibilityOff />
                        ) : (
                          <Visibility />
                        )}
                      </IconButton>
                    </InputAdornment>
                  }
                />

                {errors.password && (
                  <Typography
                    color="error"
                    variant="caption"
                    fontSize=".8rem"
                    margin=".6rem 0"
                  >
                    {errors.password}
                  </Typography>
                )}
              </FormControl>
            </ItemWrapper>
          </InnerWrapper>
        );

      case ForgotPasswordStep.CONFIRMATION:
        return (
          <InnerWrapper>
            <Typography
              variant="body1"
              sx={{ textAlign: "start", margin: "25px" }}
            >
              {t("forgot-password.changed-successfully")} <br />
              {t("forgot-password.continue-to-signin")}
            </Typography>
          </InnerWrapper>
        );
    }
  };

  return (
    <Background>
      <Container>
        <LogoContainer>
          <Logo alt="Logo" />
        </LogoContainer>

        {renderContent()}
        {!loading && (
          <ItemWrapper>
            {currentStep !== ForgotPasswordStep.CONFIRMATION && (
              <Button
                loading={AuthStore.isValidating || AuthStore.isLoading}
                fontWeight="bold"
                text={t("actions.submit")}
                onClick={handleSubmit}
              />
            )}

            <Link
              textAlign="center"
              onClick={handleSignIn}
              sx={{
                cursor: "pointer",
                display: "block",
                marginTop: "1.5rem",
              }}
            >
              {t("sign-in.back-to-signin")}
            </Link>
          </ItemWrapper>
        )}
      </Container>
    </Background>
  );
});

export default withTranslation()(withGlobalPopup(ForgotPassword));
